import $ from 'jquery';
import * as TWTmediaQuery from './twt.media-query';
import pubSub from './pubSub';
import { initFilterTags } from './filter';
import initPagination from './pagination';
import initArticleListOrder from './article-list-order';
import spinnerUtils from './spinner';

/**
 * @class Tabs
 * @export
 */
export default class Tabs {
  /**
   * Creates a new instance of Tabs.
   * @param {HTMLElement} element - HTMLElement to make into a Tabnavigation.
   * @param {Object} options - Overrides for the default settings.
   */
  constructor(element, options) {
    this.element = element;
    this.id = element.id;
    this.options = Object.assign(Tabs.defaults, options);

    this.element.classList.add('tabs--enabled');
    this.init();
  }

  /**
   * @return {void}
   */
  init() {
    this.currentSize = TWTmediaQuery.getCurrentSize();
    this.intCurrentIndex = 0;
    this.arrTabs = this.element.querySelectorAll('.tabs__name');
    this.arrContents = this.element.querySelectorAll('.tabs__content');
    this.elTabNavigation = null;
    this.preventHashchange = false;

    this.prepareMarkup();

    if (location.hash !== '' && document.getElementById(location.hash.substr(2))) {
      this.switchTabById(location.hash);
    }

    window.addEventListener('hashchange', () => {
      if (this.preventHashchange === false) {
        if (location.hash !== '' && document.getElementById(location.hash.substr(2))) {
          this.switchTabById(location.hash);
          window.scrollTo(0, 0);
        }
      }
      this.preventHashchange = false;
    });

    window.addEventListener('changed.twt.mediaQuery', (event) => {
      const newSize = event.detail.newSize;

      if (this.element.classList.contains('is--mobile-fallback')) {
        if ((newSize !== 'xs' && newSize !== 's')) {
          const elCurrentName = this.element.querySelector('.tabs__name.is--active');
          const elCurrentTab = this.element.previousElementSibling.querySelector('.is--active');
          const elCurrentContent = this.element.querySelector('.tabs__content.is--active');
          if (elCurrentName !== null) elCurrentName.classList.remove(this.options.activeClass);
          if (elCurrentTab !== null) elCurrentTab.classList.remove(this.options.activeClass);
          if (elCurrentContent !== null) {
            elCurrentContent.classList.remove(this.options.activeClass);
          }

          this.intCurrentIndex = 0;
          this.arrContents[0].classList.add(this.options.activeClass);
          this.elTabNavigation.childNodes[0].classList.add(this.options.activeClass);
          this.$elTabsClose.classList.remove(this.options.activeClass);
        } else if ((newSize === 'xs' || newSize === 's')) {
          const elCurrentContent = this.element.querySelector(`.tabs__content.${this.options.activeClass}`);
          const elCurrentTab = this.element.previousElementSibling.querySelector('.is--active');

          if (elCurrentContent !== null) {
            elCurrentContent.classList.remove(this.options.activeClass);
          }

          if (elCurrentTab !== null) elCurrentTab.classList.remove(this.options.activeClass);
          this.intCurrentIndex = null;
        }
      }
    });
  }

  /**
   * @return {void}
   */
  prepareMarkup() {
    let $elTabsClose = null;
    if (this.element.classList.contains('is--mobile-fallback')) {
      $elTabsClose = document.createElement('div');
      $elTabsClose.classList.add('tabs-close');
      const $elTabsCloseButton = document.createElement('span');
      $elTabsCloseButton.classList.add('btn', 'is--secondary', 'is--icon-left');
      $elTabsCloseButton.innerHTML = `<svg class="icon"><use xlink:href="${this.element.dataset.iconsPath}#doublearrowcircle"></use></svg><span class="btn__text">${this.element.dataset.back}</span>`;
      $elTabsCloseButton.addEventListener('click', (event) => {
        const elCurrentContent = this.arrContents[this.intCurrentIndex];
        elCurrentContent.classList.remove(this.options.activeClass);
        const elCurrentTab = this.elTabNavigation.childNodes[this.intCurrentIndex];
        elCurrentTab.classList.remove(this.options.activeClass);
        this.intCurrentIndex = null;

        event.target.closest('.tabs-close').classList.remove(this.options.activeClass);

        pubSub.publish('closed.twt.tabs');

        return false;
      });
      $elTabsClose.appendChild($elTabsCloseButton);
      this.element.parentNode.insertBefore($elTabsClose, this.element.previousSibling);
      this.$elTabsClose = $elTabsClose;
    }

    const $elTabsNavigation = document.createElement('nav');
    $elTabsNavigation.classList.add('nav', 'nav--tabs');
    if (this.element.classList.contains('is--mobile-fallback')) $elTabsNavigation.classList.add('is--mobile-fallback');

    const $elTabs = document.createElement('ul');
    $elTabs.classList.add('tabs-navigation');

    for (let index = 0, len = this.arrTabs.length; index < len; index += 1) {
      const element = this.arrTabs[index];
      const strName = element.textContent;
      const $elListItem = document.createElement('li');
      const $elTab = document.createElement('a');

      $elListItem.classList.add('tab');
      $elTab.textContent = strName;
      $elTab.href = `#!${this.id}-${index}`;

      $elTab.addEventListener('click', (event) => {
        this.switchTabById(event.target.hash);
        this.preventHashchange = true;
        return false;
      });

      if (this.element.classList.contains('is--mobile-fallback')) {
        if (index === 0 && this.currentSize !== 'xs' && this.currentSize !== 's') $elListItem.classList.add(this.options.activeClass);
      } else if (index === 0) {
        $elListItem.classList.add(this.options.activeClass);
      }

      $elListItem.appendChild($elTab);
      $elTabs.appendChild($elListItem);
      $elTabsNavigation.appendChild($elTabs);

      if (this.element.classList.contains('is--mobile-fallback')) {
        element.addEventListener('click', (event) => {
          if ($elTabsClose !== null) $elTabsClose.classList.add(this.options.activeClass);
          this.switchTabBySibling(event.target.closest('.tabs__name'));
          return false;
        });
      }
    }

    for (let index = 0, len = this.arrContents.length; index < len; index += 1) {
      const el = this.arrContents[index];
      el.id = `${this.id}-${index}`;

      if (this.element.classList.contains('is--mobile-fallback')) {
        if (index === 0 && this.currentSize !== 'xs' && this.currentSize !== 's') el.classList.add(this.options.activeClass);
      } else if (index === 0) {
        el.classList.add(this.options.activeClass);
      }
    }

    this.element.parentNode.insertBefore($elTabsNavigation, this.element);
    this.elTabNavigation = $elTabs;
  }

  /**
   * @param  {String} id selector
   * @return {void}
   */
  switchTabById(id) {
    const elCurrentContent = this.arrContents[this.intCurrentIndex];
    elCurrentContent.classList.remove(this.options.activeClass);
    const elCurrentTab = this.elTabNavigation.childNodes[this.intCurrentIndex];
    elCurrentTab.classList.remove(this.options.activeClass);

    for (let index = 0, len = this.arrContents.length; index < len; index += 1) {
      const element = this.arrContents[index];
      if (element.id === id.substr(2)) {
        element.classList.add(this.options.activeClass);
        this.intCurrentIndex = index;
        if ($(element).is('[data-link]')) {
          const token = spinnerUtils.create();

          $.get($(element).attr('data-link'), (data) => {
            if (data.length > 0) {
              const container = $(element).find('.load-more-content');
              $(element).find('.load-more-content').first().html(data);
              $(element).removeAttr('data-link');

              initFilterTags(document.querySelector('.delete-option'));
              initPagination(container);
              initArticleListOrder(container);

              pubSub.publish('reloaded.content', {
                scope: container[0],
              });
            }

            // stop spinning
            spinnerUtils.destroy(token);
          });
        }
      }
    }

    this.elTabNavigation.childNodes[this.intCurrentIndex].classList.add(this.options.activeClass);

    // update matchheight
    $.fn.matchHeight._update(); // eslint-disable-line no-underscore-dangle
  }

  /**
   * @param  {HTMLElement} element tab node
   * @return {void}
   */
  switchTabBySibling(element) {
    if (this.intCurrentIndex !== null) {
      const elCurrentContent = this.arrContents[this.intCurrentIndex];
      elCurrentContent.classList.remove(this.options.activeClass);
      const elCurrentTab = this.elTabNavigation.childNodes[this.intCurrentIndex];
      elCurrentTab.classList.remove(this.options.activeClass);
    }

    for (let index = 0, len = this.arrContents.length; index < len; index += 1) {
      const el = this.arrContents[index];
      if (el.id === element.nextElementSibling.id) {
        el.classList.add(this.options.activeClass);
        this.intCurrentIndex = index;

        if ($(element).is('[data-link]')) {
          const token = spinnerUtils.create();

          $.get($(element).attr('data-link'), (data) => {
            if (data.length > 0) {
              const container = $(element).find('.load-more-content');
              $(element).find('.load-more-content').first().html(data);
              $(element).removeAttr('data-link');

              initFilterTags(document.querySelector('.delete-option'));
              initPagination(container);
              initArticleListOrder(container);

              pubSub.publish('reloaded.content', {
                scope: container[0],
              });
            }

            // stop spinning
            spinnerUtils.destroy(token);
          });
        }
      }
    }

    element.classList.add(this.options.activeClass);
    this.elTabNavigation.childNodes[this.intCurrentIndex].classList.add(this.options.activeClass);

    // update matchheight
    $.fn.matchHeight._update(); // eslint-disable-line no-underscore-dangle
  }
}

Tabs.defaults = {
  activeClass: 'is--active',
};

$('.is--mobile-fallback dt').on('click', () => {
  document.body.classList.add('has-tab-overlay');
});

pubSub.subscribe('closed.twt.tabs', () => {
  document.body.classList.remove('has-tab-overlay');
});



// WEBPACK FOOTER //
// ./assets/js/components/tabs.js